import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogOut, User, BookOpen, Sparkles, Library, LogIn, UserPlus, Menu, X, Settings } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ANCHORS = [
  { id: "why-revivedu", label: "Why Revivedu" },
  { id: "how-it-works", label: "How It Works" },
  { id: "features", label: "Features" },
];

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    setMobileOpen(false);
    navigate("/");
  };

  const scrollToSection = (id) => {
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 120);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
    <header className="bg-background/95 backdrop-blur border-b border-border sticky top-0 z-50" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
        <div className="flex items-center justify-between gap-4">
          <Link
            to="/"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity flex-shrink-0"
            data-testid="header-home-link"
            onClick={() => setMobileOpen(false)}
          >
            <img
              src="/revivedu-logo.png"
              alt="Revivedu - Reviving the Joy of Learning"
              className="h-10 sm:h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1 lg:gap-2">
            {isAuthenticated ? (
              <>
                <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="rounded-full text-secondary" data-testid="header-dashboard-link">
                  <User className="mr-2 h-4 w-4" /> Dashboard
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate("/generate")} className="rounded-full text-secondary" data-testid="header-generate-link">
                  <Sparkles className="mr-2 h-4 w-4" /> Generate
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate("/library")} className="rounded-full text-secondary" data-testid="header-library-link">
                  <Library className="mr-2 h-4 w-4" /> Library
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate("/guide")} className="rounded-full text-secondary" data-testid="header-guide-link">
                  <BookOpen className="mr-2 h-4 w-4" /> Guide
                </Button>
              </>
            ) : (
              ANCHORS.map((a) => (
                <button
                  key={a.id}
                  onClick={() => scrollToSection(a.id)}
                  className="px-3 py-2 rounded-full text-sm font-medium text-secondary hover:bg-muted transition-colors"
                  data-testid={`header-nav-${a.id}`}
                >
                  {a.label}
                </button>
              ))
            )}
          </nav>

          {/* Right side actions (desktop) */}
          <div className="hidden md:flex items-center gap-2 flex-shrink-0">
            {isAuthenticated ? (
              <>
                {user && <span className="text-sm text-foreground/60">Welcome, {user.name}</span>}
                <Button variant="ghost" size="sm" onClick={() => navigate("/account")} className="rounded-full text-secondary" data-testid="header-account-btn" aria-label="Account settings">
                  <Settings className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={handleLogout} className="rounded-full border-2 border-destructive/50 text-destructive hover:bg-destructive hover:text-white" data-testid="header-logout-btn">
                  <LogOut className="mr-2 h-4 w-4" /> Logout
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => navigate("/login")} className="rounded-full border-2 border-secondary text-secondary hover:bg-secondary hover:text-white" data-testid="header-login-btn">
                  <LogIn className="mr-2 h-4 w-4" /> Login
                </Button>
                <Button size="sm" onClick={() => navigate("/signup")} className="rounded-full bg-primary hover:bg-primary/90 text-white" data-testid="header-signup-btn">
                  <UserPlus className="mr-2 h-4 w-4" /> Sign Up
                </Button>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
        <button
          className="md:hidden p-2 rounded-lg text-secondary hover:bg-muted relative z-[70]"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
          aria-expanded={mobileOpen}
          data-testid="header-hamburger-btn"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
        </div>
      </div>
      </header>

      {/* Mobile slide-in drawer + overlay (outside header to escape backdrop-blur containing block) */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-[60]" data-testid="header-mobile-overlay">
          {/* Backdrop - closes on outside tap */}
          <button
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
            className="absolute inset-0 bg-secondary/40 backdrop-blur-sm animate-in fade-in duration-200"
            data-testid="header-mobile-backdrop"
          />
          {/* Drawer panel */}
          <div
            className="absolute right-0 top-0 h-full w-72 max-w-[80%] bg-[#FDFBF7] border-l border-border shadow-2xl flex flex-col gap-1 p-4 pt-20 animate-in slide-in-from-right duration-300"
            data-testid="header-mobile-menu"
          >
            {isAuthenticated ? (
              <>
                <Button variant="ghost" onClick={() => { setMobileOpen(false); navigate("/dashboard"); }} className="justify-start rounded-xl text-secondary text-base py-6" data-testid="header-mobile-dashboard">
                  <User className="mr-3 h-5 w-5" /> Dashboard
                </Button>
                <Button variant="ghost" onClick={() => { setMobileOpen(false); navigate("/generate"); }} className="justify-start rounded-xl text-secondary text-base py-6" data-testid="header-mobile-generate">
                  <Sparkles className="mr-3 h-5 w-5" /> Generate Activity
                </Button>
                <Button variant="ghost" onClick={() => { setMobileOpen(false); navigate("/library"); }} className="justify-start rounded-xl text-secondary text-base py-6" data-testid="header-mobile-library">
                  <Library className="mr-3 h-5 w-5" /> Library
                </Button>
                <Button variant="ghost" onClick={() => { setMobileOpen(false); navigate("/guide"); }} className="justify-start rounded-xl text-secondary text-base py-6" data-testid="header-mobile-guide">
                  <BookOpen className="mr-3 h-5 w-5" /> Guide
                </Button>
                <Button variant="ghost" onClick={() => { setMobileOpen(false); navigate("/account"); }} className="justify-start rounded-xl text-secondary text-base py-6" data-testid="header-mobile-account">
                  <Settings className="mr-3 h-5 w-5" /> Account
                </Button>
                <div className="mt-auto">
                  <Button variant="outline" onClick={handleLogout} className="w-full justify-start rounded-xl border-2 border-destructive/50 text-destructive text-base py-6" data-testid="header-mobile-logout">
                    <LogOut className="mr-3 h-5 w-5" /> Logout
                  </Button>
                </div>
              </>
            ) : (
              <>
                {ANCHORS.map((a) => (
                  <button key={a.id} onClick={() => scrollToSection(a.id)} className="text-left px-4 py-3 rounded-xl text-secondary font-medium hover:bg-muted" data-testid={`header-mobile-${a.id}`}>
                    {a.label}
                  </button>
                ))}
                <Button variant="outline" onClick={() => { setMobileOpen(false); navigate("/login"); }} className="justify-start rounded-xl border-2 border-secondary text-secondary mt-2 py-6" data-testid="header-mobile-login">
                  <LogIn className="mr-3 h-5 w-5" /> Login
                </Button>
                <Button onClick={() => { setMobileOpen(false); navigate("/signup"); }} className="justify-start rounded-xl bg-primary text-white py-6" data-testid="header-mobile-signup">
                  <UserPlus className="mr-3 h-5 w-5" /> Sign Up
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
